import type { TableColumnsType } from "antd";
import { customerApi } from "api/customer.api";
import { AppTable } from "components/antd-ui";
import { CodeCell } from "components/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { OrderDebt, OrderDebtStatus } from "types/order";
import { formatMoney } from "views/Opportunity/opportunity.meta";
import { debtStatusLabel, isDebtOverdue, toDisplayDate } from "views/Order/order.meta";

type CustomerDebt = OrderDebt & { order?: { id: number; code: string } };
type DebtSummary = { totalAmount: number; paidAmount: number; unpaidAmount: number };

const emptySummary: DebtSummary = { totalAmount: 0, paidAmount: 0, unpaidAmount: 0 };

// Tab "Công nợ": các đợt thu của mọi đơn hàng — ngày dự kiến, ngày thực tế, trạng thái thanh toán
export const CustomerDebtTab = ({ customerId }: { customerId: number }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<CustomerDebt[]>([]);
  const [summary, setSummary] = useState<DebtSummary>(emptySummary);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await customerApi.findDebts(customerId);
      setItems(res.data.orderDebts || []);
      setSummary(res.data.summary || emptySummary);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo<TableColumnsType<CustomerDebt>>(
    () => [
      {
        title: "Mã đơn",
        key: "order",
        width: 110,
        render: (_, record) => <CodeCell value={record.order?.code || "--"} />,
      },
      { title: "Nội dung đợt", dataIndex: "content", key: "content", render: (value) => value || "--" },
      {
        title: "%",
        dataIndex: "percent",
        key: "percent",
        width: 70,
        align: "right",
        render: (value: number) => (value ? `${value}%` : "--"),
      },
      {
        title: "Số tiền",
        dataIndex: "amount",
        key: "amount",
        width: 140,
        align: "right",
        render: (value: number) => formatMoney(value || 0),
      },
      {
        title: "Ngày thu dự kiến",
        dataIndex: "dueDate",
        key: "dueDate",
        width: 140,
        render: (value: string, record) => (
          <span className={isDebtOverdue(record) ? "customerDebt--overdue" : undefined}>
            {toDisplayDate(value) || "--"}
          </span>
        ),
      },
      {
        title: "Ngày thu thực tế",
        dataIndex: "collectedDate",
        key: "collectedDate",
        width: 140,
        render: (value: string) => toDisplayDate(value) || "--",
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 130,
        render: (value: OrderDebtStatus, record) => {
          const overdue = isDebtOverdue(record);
          const tone = value === OrderDebtStatus.Paid ? "paid" : overdue ? "overdue" : "unpaid";
          return (
            <span className={`customerDebtStatus customerDebtStatus--${tone}`}>
              {overdue ? "Quá hạn" : debtStatusLabel[value] || "--"}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div>
      <div className="customerDebtSummary">
        <div className="customerDebtSummary__item">
          <span className="customerDebtSummary__label">Tổng phải thu</span>
          <span className="customerDebtSummary__value">{formatMoney(summary.totalAmount)}</span>
        </div>
        <div className="customerDebtSummary__item">
          <span className="customerDebtSummary__label">Đã thu</span>
          <span className="customerDebtSummary__value customerDebtSummary__value--paid">
            {formatMoney(summary.paidAmount)}
          </span>
        </div>
        <div className="customerDebtSummary__item">
          <span className="customerDebtSummary__label">Còn nợ</span>
          <span className="customerDebtSummary__value customerDebtSummary__value--unpaid">
            {formatMoney(summary.unpaidAmount)}
          </span>
        </div>
      </div>
      <AppTable<CustomerDebt>
        className="customerTabTable"
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={items.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        locale={{ emptyText: "Chưa có công nợ" }}
        rowClassName={(record) => (isDebtOverdue(record) ? "customerDebtRow--overdue" : "")}
      />
    </div>
  );
};
